import type { NavPalette } from "@/components/design-switcher/nav-palette";

export const c = {
  board: "#1B3329",
  boardLight: "#2D4A3E",
  boardMid: "#243D33",
  chalk: "#F0EDE4",
  chalkDim: "#C8C3B8",
  chalkFaint: "rgba(240,237,228,0.12)",
  chalkGlow: "rgba(240,237,228,0.25)",
  flamenco: "#F05260",
  flamencoSoft: "rgba(240,82,96,0.85)",
  skyfall: "#84D5F7",
  skyfallSoft: "rgba(132,213,247,0.85)",
  mintleaf: "#4EB969",
  mintleafSoft: "rgba(78,185,105,0.85)",
  plumwood: "#842885",
  plumwoodSoft: "rgba(132,40,133,0.85)",
  white: "#FFFFFF",
  tray: "#3D2B1A",
  trayLight: "#5A4231",
  eraserPink: "#F7B4BA",
  eraserFelt: "#8C8C8C",
  ruledLine: "rgba(240,237,228,0.08)",
} as const;

export const nav: NavPalette = {
  surface: "rgba(27, 51, 41, 0.9)",
  surfaceHover: "rgba(45, 74, 62, 0.94)",
  border: "rgba(240, 237, 228, 0.3)",
  text: "#F0EDE4",
  textMuted: "rgba(240, 237, 228, 0.75)",
  activeBg: "rgba(240, 237, 228, 0.18)",
  activeText: "#F0EDE4",
};

export const chalkShadow =
  "0 0 3px rgba(255,255,255,0.4), 0 0 8px rgba(255,255,255,0.15), 1px 1px 0 rgba(0,0,0,0.15)";
export const chalkShadowBold =
  "0 0 5px rgba(255,255,255,0.5), 0 0 12px rgba(255,255,255,0.2), 1px 1px 0 rgba(0,0,0,0.2)";
export const chalkShadowColor = (color: string) =>
  `0 0 5px ${color}44, 0 0 12px ${color}22, 1px 1px 0 rgba(0,0,0,0.15)`;

interface BookData {
  title: string;
  description: string;
  accentColor: string;
  level: string;
  emoji: string;
  tags: string[];
}

export const FEATURED_BOOK: BookData = {
  title: "The Brave Little Fox",
  description:
    "Follow Fern the fox as she discovers that being brave doesn't mean having no fear — it means reading one more page even when the words feel tricky. A heartwarming tale perfect for building fluency and confidence through the EAR method.",
  accentColor: c.flamenco,
  level: "Grade 1-2",
  emoji: "\uD83E\uDD8A",
  tags: ["Fluency", "Courage", "Sight Words"],
};

export const LIBRARY_BOOKS: BookData[] = [
  {
    title: "Ocean ABC",
    description:
      "Dive into an undersea alphabet adventure with guided reading.",
    accentColor: c.skyfall,
    level: "Grade 1",
    emoji: "\uD83D\uDC20",
    tags: ["Alphabet", "Ocean", "Phonics"],
  },
  {
    title: "Garden Helpers",
    description: "Discover how bugs and birds work together in a sunny garden.",
    accentColor: c.mintleaf,
    level: "Grade 2",
    emoji: "\uD83C\uDF3B",
    tags: ["Science", "Nature", "Vocabulary"],
  },
  {
    title: "Star Stories",
    description:
      "Ancient tales retold with modern sight-word practice woven in.",
    accentColor: c.plumwood,
    level: "Grade 3",
    emoji: "\u2B50",
    tags: ["Myths", "Fluency", "Comprehension"],
  },
];

interface ValuePropData {
  icon: string;
  title: string;
  body: string;
  accentColor: string;
}

export const VALUE_PROPS: ValuePropData[] = [
  {
    icon: "\uD83D\uDC42",
    title: "Engaged Listening",
    body: "Children listen to fluent narration while following along, building natural reading rhythm and sight vocabulary through the power of audio-guided learning.",
    accentColor: c.skyfall,
  },
  {
    icon: "\uD83D\uDCD6",
    title: "Aided Reading",
    body: "Word-by-word highlighting syncs with narration, connecting sounds to text. Each step builds on the last, creating confident readers one word at a time.",
    accentColor: c.mintleaf,
  },
  {
    icon: "\uD83D\uDE80",
    title: "Reading Mastery",
    body: "From guided listening to independent reading, the EAR method unlocks your child's potential. Proven results for grades 1\u20133 with measurable progress tracking.",
    accentColor: c.flamenco,
  },
];
